import {
  Component,
  Suspense,
  useCallback,
  useState,
  useTransition,
  type ErrorInfo,
  type ReactNode,
} from 'react'
import * as Sentry from '@sentry/react'

import ErrorFallback from '@/containers/App/ErrorFallback'
import Loading from '@/containers/App/Loading'
import { configureSentry } from '@/services/sentry'

/** The details the fallback shows and Sentry receives as `extra`. */
type ErrorDetails = {
  code?: number
  file?: string
  line?: number
  message: string
  details: string[]
  timestamp: string
  url: string
  userAgent: string
}

/** Non-standard fields some engines (Firefox, old Edge) add to an Error. */
type EngineError = Error & {
  number?: number
  fileName?: string
  lineNumber?: number
}

type FallbackRenderProps = {
  error: ErrorDetails | null
  rawError: Error | null
  refreshPage: boolean
}

type ErrorBoundaryProps = {
  refreshPage?: boolean
  fallback?: ReactNode
  fallbackRender?: (props: FallbackRenderProps) => ReactNode
  children: ReactNode
}

type ErrorBoundaryWrapperProps = {
  onError: (error: Error, errorInfo: ErrorInfo) => void
  children: ReactNode
}

type ErrorBoundaryWrapperState = {
  hasError: boolean
}

class ErrorBoundaryWrapper extends Component<
  ErrorBoundaryWrapperProps,
  ErrorBoundaryWrapperState
> {
  static getDerivedStateFromError(): ErrorBoundaryWrapperState {
    return { hasError: true }
  }

  override state: ErrorBoundaryWrapperState = { hasError: false }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props
    onError(error, errorInfo)
  }

  override render() {
    const { hasError } = this.state
    const { children } = this.props

    // The parent renders the error UI.
    return hasError ? null : children
  }
}

export const ErrorBoundary = ({
  refreshPage = true,
  fallback,
  fallbackRender,
  children,
}: ErrorBoundaryProps) => {
  const [hasError, setHasError] = useState(false)
  const [error, setError] = useState<ErrorDetails | null>(null)
  const [rawError, setRawError] = useState<Error | null>(null)
  const [isPending, startTransition] = useTransition()
  const [errorLogged, setErrorLogged] = useState(false)

  const handleError = useCallback(
    (err: EngineError, info: ErrorInfo) => {
      try {
        const componentStack = info.componentStack ?? ''
        const details = componentStack.trim().split('\n')
        const errorDetails: ErrorDetails = {
          code: err.number,
          file: err.fileName,
          line: err.lineNumber,
          message: `${err.message} ${details.shift()}`,
          details,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
        }

        // Non-urgent UI updates go through startTransition.
        startTransition(() => {
          setRawError(err)
          setError(errorDetails)
          setHasError(true)
        })

        // Report to Sentry once: React 19 does not rethrow an error a
        // boundary catches, so Sentry's global handlers never see it. The
        // entry starts Sentry at idle time, so a first-render error can land
        // first; start it now (a no-op once started). Only the component
        // stack is added: Sentry already records the page URL and browser.
        if (!errorLogged) {
          try {
            configureSentry()
          } catch (sentryError) {
            console.error('Sentry init failed', sentryError)
          }
          Sentry.captureException(err, {
            contexts: { react: { componentStack } },
          })
          setErrorLogged(true)
        }

        console.group('Fatal Error Details')
        console.error('Error:', err)
        console.error('Component Stack:', componentStack)
        console.error('Additional Details:', errorDetails)
        console.groupEnd()
      } catch (loggingError) {
        console.error('Error while handling error:', loggingError)
      }
    },
    [errorLogged]
  )

  if (isPending) {
    return <Loading />
  }

  if (hasError) {
    if (fallbackRender) {
      return fallbackRender({ error, rawError, refreshPage })
    }
    return (
      <Suspense fallback={<Loading />}>
        {fallback ?? <ErrorFallback refreshPage={refreshPage} error={error} />}
      </Suspense>
    )
  }

  return (
    <ErrorBoundaryWrapper onError={handleError}>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </ErrorBoundaryWrapper>
  )
}
